import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MenuType } from '../../../../system/system-dtos';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { isDefined } from '../../../../components/utils/utils';
import { BlastV2Service } from '../../../../components/views/main-table-v2/service/blast-v2.service';
import { filter, switchMap, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { MenuNode } from '../tree-sidenav.component';
import { AlertService } from '../../../../feature/manage/player/alerts/alert.service';
import { DetailedActiveAlertDTO } from '../../../../feature/manage/player/alerts/models/alerts.model';
import { DocumentsService } from '../../../../feature/manage/player/documents/documents.service';
import {
  DocumentRequestsCountDTO,
  DocumentsRequestStatus,
} from '../../../../feature/manage/player/documents/documents.model';
import { DuplicateChecksService } from '../../../../feature/manage/player/duplicate-checks/duplicate-checks.service';
import { FeatureFlagsService } from '../../../../feature/manage/player/feature-flags/feature-flags.service';

@Component({
  selector: 'app-tree-sidenav-menu-item',
  templateUrl: './tree-sidenav-menu-item.component.html',
  styleUrls: ['./tree-sidenav-menu-item.component.scss'],
  standalone: false,
})
export class TreeSidenavMenuItemComponent implements OnInit {
  @Input() menuNode: MenuNode;

  public amountOfTickers: number = 0;
  private subscription: Subscription;
  private tickerCounterMapping: Map<string, string> = new Map<string, string>();
  active: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private blastV2Service: BlastV2Service,
    private detectorRef: ChangeDetectorRef,
    private alertService: AlertService,
    private documentsService: DocumentsService,
    private duplicateChecksService: DuplicateChecksService
  ) {
    this.tickerCounterMapping.set('depositticker', 'depositticker');
    this.tickerCounterMapping.set('withdrawticker', 'withdrawticker');
    this.tickerCounterMapping.set('alerts', 'alerts');
    this.tickerCounterMapping.set('documents', 'documents');
    this.tickerCounterMapping.set('linkedaccounts', 'linkedaccounts');
  }

  ngOnInit(): void {
    if (this.showTickerCounter()) {
      this.subscription = this.blastV2Service
        .getCollectionObservable(this.tickerCounterMapping.get(this.menuNode.code))
        .pipe(filter((d) => d?.length > 0))
        .subscribe((d) => {
          this.amountOfTickers = d?.length;
          this.detectorRef.detectChanges();
        });
      this.checkForNonWsCounterTicker();
    }
    this.active = this.isActive();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.active = this.isActive();
      }
    });
  }

  checkForNonWsCounterTicker() {
    if (this.menuNode.code === 'alerts') {
      this.subscription = this.alertService
        .getCollectionObservable('active-alerts')
        .pipe
        // startWith('initialization')
        ()
        .subscribe(() => {
          this.alertService.getActiveAlerts().subscribe((activeAlers: DetailedActiveAlertDTO[]) => {
            this.amountOfTickers = activeAlers.length ?? 0;
            this.detectorRef.detectChanges();
          });
        });
    }
    if (this.menuNode.code === 'documents') {
      this.subscription = this.documentsService.docRequestStatusObservable().subscribe(() => {
        this.documentsService
          .getDocumentsRequestsCount({ status: DocumentsRequestStatus.SUBMITTED })
          .subscribe((counter: DocumentRequestsCountDTO) => {
            this.amountOfTickers = counter.count ?? 0;
            this.detectorRef.detectChanges();
          });
      });
    }
    if (this.menuNode.code === 'linkedaccounts') {
      this.sortOutInitialCountForLinkedAccounts();
      this.reactOnLinkAccountAdded();
      this.reactOnLinkAccountResolved();
    }
  }

  private reactOnLinkAccountAdded() {
    return (this.subscription = this.duplicateChecksService.duplicateCheckCreatedObservable().subscribe((obs) => {
      if (obs && !obs.finished) {
        this.amountOfTickers = this.amountOfTickers + 1;
        this.detectorRef.detectChanges();
      }
    }));
  }

  private reactOnLinkAccountResolved() {
    return (this.subscription = this.duplicateChecksService.duplicateCheckArchivedObservable().subscribe((obs) => {
      if (obs && obs.archivedDate) {
        const newAmount = this.amountOfTickers - 1;
        this.amountOfTickers = newAmount < 0 ? 0 : newAmount;
        this.detectorRef.detectChanges();
      }
    }));
  }

  private sortOutInitialCountForLinkedAccounts() {
    this.duplicateChecksService
      .getPendingReviewDuplicateChecks({ pageNumber: 1, pageSize: 1 }, {})
      .pipe(take(1))
      .subscribe((page) => {
        this.amountOfTickers = page?.total ?? 0;
        this.detectorRef.detectChanges();
      });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  getUrl() {
    return TreeSidenavMenuItemComponent.getNavigationUrl(this.menuNode);
  }

  showTickerCounter(): boolean {
    return this.tickerCounterMapping.has(this.menuNode.code);
  }

  isActive(): boolean {
    let urlTree = isDefined(this.menuNode.subNav)
      ? this.router.createUrlTree(
          [TreeSidenavMenuItemComponent.getNavigationUrl({ ...this.menuNode, menuItem: undefined, code: '' })],
          { relativeTo: this.route }
        ) // url without first active tab
      : this.router.createUrlTree([TreeSidenavMenuItemComponent.getNavigationUrl(this.menuNode)], {
          relativeTo: this.route,
        });
    return this.router.isActive(urlTree, {
      fragment: 'ignored',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      paths: 'subset',
    });
  }

  public static getNavigationUrl(menuNode: MenuNode): string {
    if (isDefined(menuNode.subNav)) {
      let firstActiveTab = this.getNavigationUrl({ ...menuNode, subNav: undefined }).replace('./', '');
      return `./nav/${menuNode.subNav}/${firstActiveTab}`;
    }
    if (!isDefined(menuNode.menuItem)) {
      return `./${menuNode.code}`;
    }
    if (-1 < menuNode.code.indexOf('ticker')) {
      return `./v2/${menuNode.code}`;
    }
    if (
      [MenuType.MAINTAIN, MenuType.ENTITY_GROUP, MenuType.ELASTIC, MenuType.TICKER, MenuType.CASSANDRA].includes(
        menuNode.menuItem.menuType
      )
    ) {
      return `./${menuNode.menuItem.code}`;
    }
    if (menuNode.menuItem.menuType === MenuType.MULTIPLE) {
      return `./${menuNode.menuItem.code}`;
    }
    if (menuNode.menuItem.menuType === MenuType.DASHBOARD) {
      return `./dashboard/${menuNode.menuItem.code}`;
    }
    if (menuNode.menuItem.menuType === MenuType.ENTITY_GROUP_SINGLE_FORM && menuNode.menuItem.entityGroup.entryForm) {
      return `./${menuNode.menuItem.code}/single/report`;
    }
    if (menuNode.menuItem.menuType === MenuType.ENTITY_GROUP_SINGLE_FORM && menuNode.subCode) {
      return `./${menuNode.menuItem.code}/single/${menuNode.subCode}`;
    }
    if (menuNode.menuItem.menuType === MenuType.ENTITY_GROUP_SINGLE_FORM) {
      return `./${menuNode.menuItem.code}/single/overall`;
    }
  }
}
